import React, { useMemo, useState } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Chip, Tooltip, Box, Typography, TableContainer, Collapse
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// --- HELPERS ---
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  } catch {
    return iso;
  }
};

const mask = (pwd) => (pwd ? "•".repeat(8) : "-");

const getVehiculosUsuario = (row) => (Array.isArray(row?.vehiculos) ? row.vehiculos : []);

const getVehiculosTooltip = (row) => {
  const vehiculos = getVehiculosUsuario(row);
  if (!vehiculos.length) return "Sin vehiculos";
  const nombres = vehiculos
    .slice(0, 12)
    .map((v) => {
      const nombre = v?.nm || v?.name || v?.uid || v?.id;
      const grupos = Array.isArray(v?.grupos) && v.grupos.length
        ? ` (${v.grupos.map((g) => g?.nm || g?.name || g?.id).filter(Boolean).join(", ")})`
        : "";
      return nombre ? `${nombre}${grupos}` : "";
    })
    .filter(Boolean);
  const extra = vehiculos.length > nombres.length ? `, +${vehiculos.length - nombres.length}` : "";
  return `Total: ${vehiculos.length} | Directos: ${row?.vehiculos_directos || 0} | Grupos: ${row?.vehiculos_grupos || 0} - ${nombres.join(", ")}${extra}`;
};

const getDeleteBlockReason = (row) => {
  const total = Number(row?.cantidad_vehiculos ?? getVehiculosUsuario(row).length) || 0;
  const directos = Number(row?.vehiculos_directos || 0) || 0;
  const grupos = Number(row?.vehiculos_grupos || 0) || 0;

  if (total <= 0 && directos <= 0 && grupos <= 0) return "";

  return `No se puede eliminar: tiene ${total} unidad(es) activa(s), ${directos} directa(s) y ${grupos} en grupo(s). Quite sus unidades o grupos activos primero.`;
};

export default function LeasecontrolUsuariosTable({
  rows = [],          // modo normal
  groups = null,      // modo agrupado (array de grupos)
  onEdit,
  onDelete,
  onDetail,
  idLabel = "LeaseControl ID",
  showDelete = true,
}) {
  const [revealById, setRevealById] = useState({});
  const [openGroup, setOpenGroup] = useState({}); // key => boolean

  const toggleReveal = (id) => setRevealById((p) => ({ ...p, [id]: !p[id] }));
  const toggleGroup = (key) => setOpenGroup((p) => ({ ...p, [key]: !(p[key] ?? true) }));

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  // ✅ Normalizamos a grupos siempre (si no pasan groups, generamos 1 grupo "Todos")
  const normalizedGroups = useMemo(() => {
    if (Array.isArray(groups)) {
      return groups.map((g, idx) => ({
        key: String(g?.cod_servicio_cliente ?? `__NO_LINK__${idx}`),
        cod_servicio_cliente: g?.cod_servicio_cliente ?? null,
        razon_social: g?.razon_social ?? (g?.cod_servicio_cliente ? "-" : "No vinculados"),
        descripcion: g?.descripcion ?? "-",
        usuarios: Array.isArray(g?.usuarios) ? g.usuarios : [],
      }));
    }

    return [
      {
        key: "__ALL__",
        cod_servicio_cliente: null,
        razon_social: "Listado",
        descripcion: "Usuarios",
        usuarios: Array.isArray(rows) ? rows : [],
      },
    ];
  }, [groups, rows]);

  const renderUserRow = (r, { hideClientCols = false } = {}) => {
    const local = r?.local || null;
    const id = r?.id;
    const pwd = local?.external_password || "";
    const revealed = !!revealById[id];
    const estadoRaw = String(local?.estado || "").toLowerCase();
    const deleteBlockReason = getDeleteBlockReason(r);

    return (
      <TableRow key={id} hover>
        <TableCell sx={{ fontWeight: 500, color: "text.secondary" }}>{id}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{r?.nm || "-"}</TableCell>

        {/* Cliente */}
        <TableCell sx={{ minWidth: 220 }}>
          {hideClientCols ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>
          ) : local?.razon_social ? (
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {local.razon_social}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>-</Typography>
          )}
        </TableCell>

        {/* Descripción */}
        <TableCell sx={{ minWidth: 220 }}>
          {hideClientCols ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>
          ) : local?.descripcion ? (
            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.2 }}>
              {local.descripcion}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>-</Typography>
          )}
        </TableCell>

        {/* Password */}
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", minWidth: 70, fontSize: "0.85rem" }}
            >
              {revealed ? pwd : mask(pwd)}
            </Typography>
            <IconButton size="small" onClick={() => toggleReveal(id)} disabled={!pwd}>
              {revealed ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
            </IconButton>
            <IconButton size="small" onClick={() => copyToClipboard(pwd)} disabled={!pwd}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </TableCell>

        <TableCell>{local?.creado_por ?? "-"}</TableCell>
        <TableCell align="center">
          <Tooltip title={getVehiculosTooltip(r)}>
            <Chip
              size="small"
              label={r?.cantidad_vehiculos ?? getVehiculosUsuario(r).length}
              sx={{ height: 20, minWidth: 38, fontSize: "0.7rem", fontWeight: 900 }}
            />
          </Tooltip>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(local?.fecha_registro)}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(local?.last_sync)}</TableCell>

        <TableCell align="center">
          <Chip
            label={
              estadoRaw === "activo"
                ? "Activo"
                : estadoRaw === "inactivo"
                ? "Inactivo"
                : (local?.estado || "-")
            }
            size="small"
            color={estadoRaw === "activo" ? "success" : "default"}
            variant="filled"
            sx={{
              fontSize: "0.65rem",
              fontWeight: "bold",
              height: 20,
              borderRadius: "4px",
              minWidth: 70,
              textTransform: "uppercase",
            }}
          />
        </TableCell>

        <TableCell align="right">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Detalle e historial">
              <IconButton size="small" color="info" onClick={() => onDetail?.(r)}>
                <VisibilityIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" color="primary" onClick={() => onEdit?.(r)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            {showDelete && (
              <Tooltip title={deleteBlockReason || "Eliminar"}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete?.(r)}
                    disabled={!!deleteBlockReason}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const isGroupedMode = Array.isArray(groups);

  return (
    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
      <Table
        size="small"
        stickyHeader
        sx={{
          minWidth: 1200,
          "& .MuiTableCell-root": { fontSize: "0.82rem", py: 0.8, px: 1 },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: "bold",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.background.paper
                    : theme.palette.grey[100],
              },
            }}
          >
            <TableCell>{idLabel}</TableCell>
            <TableCell>Usuario</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell>Contraseña</TableCell>
            <TableCell>Creado por</TableCell>
            <TableCell align="center">Vehículos</TableCell>
            <TableCell>Registro</TableCell>
            <TableCell>Últ. Sync</TableCell>
            <TableCell align="center">Estado</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {normalizedGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 3, color: "text.secondary" }}>
                No hay usuarios disponibles
              </TableCell>
            </TableRow>
          ) : (
            normalizedGroups.map((g) => {
              const key = g.key;
              const open = openGroup[key] ?? true;

              // En modo normal no mostramos cabeceras de grupo
              if (!isGroupedMode) {
                return g.usuarios.map((u) => renderUserRow(u));
              }

              return (
                <React.Fragment key={key}>
                  {/* Cabecera del grupo */}
                  <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                    <TableCell colSpan={11}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <IconButton size="small" onClick={() => toggleGroup(key)}>
                          {open ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                        </IconButton>

                        <Typography sx={{ fontWeight: 800 }}>
                          {g.razon_social || "-"}
                        </Typography>

                        {g.cod_servicio_cliente ? (
                          <Chip size="small" label={`Servicio: ${g.cod_servicio_cliente}`} sx={{ fontSize: "0.7rem" }} />
                        ) : (
                          <Chip size="small" label="No vinculados" sx={{ fontSize: "0.7rem" }} />
                        )}

                        <Typography sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                          {g.descripcion || "-"}
                        </Typography>

                        <Chip size="small" label={`${g.usuarios.length} usuario(s)`} sx={{ fontSize: "0.7rem" }} />
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Filas del grupo */}
                  <TableRow>
                    <TableCell colSpan={11} sx={{ p: 0, borderBottom: "none" }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.82rem", py: 0.8, px: 1 } }}>
                          <TableBody>
                            {g.usuarios.map((u) =>
                              // en modo agrupado, evitamos repetir cliente/descripcion en cada fila
                              renderUserRow(u, { hideClientCols: true })
                            )}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
